import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';

const API = import.meta.env.VITE_API_URL || '/api';

interface TodayStats {
  salesCount: number;
  totalSales: number;
}

interface MonthStats {
  salesCount: number;
  totalSales: number;
}

interface InventoryStats {
  productCount: number;
  lowStockCount: number;
}

interface BillingStats {
  invoiceCount: number;
  totalInvoiced: number;
  pendingInvoices: number;
}

interface DashboardStats {
  today: TodayStats;
  month: MonthStats;
  inventory: InventoryStats;
  billing: BillingStats;
}

interface CashflowRegister {
  registerId: string;
  name: string;
  type: string;
  currentBalance: string;
  pendingSettlements: number;
  pendingNet: string;
  projectedBalance: string;
}

interface TimelineFlow {
  registerName?: string;
  type?: string;
  poNumber?: string;
  net?: string;
  amount?: string;
}

interface TimelineItem {
  date: string;
  totalInflow?: string;
  totalOutflow?: string;
  netFlow?: string;
  inflows?: TimelineFlow[];
  outflows?: TimelineFlow[];
}

const modules = [
  { name: 'Productos', icon: '📦', path: '/products' },
  { name: 'Punto de Venta', icon: '🛒', path: '/pos' },
  { name: 'Caja', icon: '💵', path: '/cash' },
  { name: 'Clientes', icon: '👥', path: '/customers' },
  { name: 'Proveedores', icon: '🚚', path: '/suppliers' },
  { name: 'Órdenes de Compra', icon: '📋', path: '/purchase-orders' },
  { name: 'Facturas', icon: '🧾', path: '/invoices' },
  { name: 'Reportes', icon: '📊', path: '/reports' },
];

function formatMoney(value: number | string | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
}

function formatNumber(value: number | undefined): string {
  return Number(value || 0).toLocaleString('es-AR');
}

function getRegisterIcon(type: string): string {
  if (type === 'cash_drawer') return '💵';
  if (type === 'bank_account') return '🏦';
  if (type === 'safe') return '🔒';
  return '📋';
}

function KpiCard({
  label,
  value,
  subtitle,
  subtitleAccent,
  icon,
  colorClass,
  borderColor,
}: {
  label: string;
  value: string;
  subtitle: string;
  subtitleAccent?: boolean;
  icon: string;
  colorClass: string;
  borderColor: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5 border-l-4 min-h-[120px] flex flex-col justify-between" style={{ borderLeftColor: borderColor }}>
      <div className="flex items-start justify-between">
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 font-medium uppercase tracking-wide">{label}</p>
        <span className={`text-xl sm:text-2xl ${colorClass}`}>{icon}</span>
      </div>
      <div>
        <p className={`text-2xl sm:text-3xl font-bold mt-2 ${colorClass}`}>{value}</p>
        <p className={`text-xs sm:text-sm mt-1 ${subtitleAccent ? 'text-red-600 font-semibold' : 'text-gray-500 dark:text-gray-400 dark:dark:text-gray-400'}`}>
          {subtitle}
        </p>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5 min-h-[120px]">
      <div className="flex items-start justify-between">
        <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
        <div className="h-6 w-6 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="h-8 w-32 bg-gray-200 rounded mt-4 animate-pulse" />
      <div className="h-3 w-20 bg-gray-200 rounded mt-2 animate-pulse" />
    </div>
  );
}

function EmptyState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
      <p className="text-4xl mb-3">📭</p>
      <p className="text-gray-600 dark:text-gray-300 font-medium">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors min-h-[44px]"
        >
          Reintentar
        </button>
      )}
    </div>
  );
}

export function Dashboard() {
  const userStr = localStorage.getItem('user');
  const user = userStr ? (JSON.parse(userStr) as { name?: string; email?: string; role?: string }) : null;
  const isCashier = user?.role === 'cashier';

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [cashflow, setCashflow] = useState<CashflowRegister[]>([]);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  async function loadDashboard() {
    setLoading(true);
    setError(null);
    try {
      if (isCashier) {
        setLoading(false);
        return;
      }
      const [dashRes, cashRes, timeRes] = await Promise.all([
        fetch(`${API}/reports/dashboard`, { headers }),
        fetch(`${API}/cash/cashflow`, { headers }),
        fetch(`${API}/cash/cashflow/timeline`, { headers }),
      ]);

      if (!dashRes.ok) {
        const err = await dashRes.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar el dashboard');
      }

      const dashData = (await dashRes.json()) as { data: DashboardStats };
      setStats(dashData.data);

      if (cashRes.ok) {
        const cashData = (await cashRes.json()) as { data?: CashflowRegister[] };
        setCashflow(cashData.data || []);
      } else {
        setCashflow([]);
      }

      if (timeRes.ok) {
        const timeData = (await timeRes.json()) as { data?: TimelineItem[] };
        setTimeline(timeData.data || []);
      } else {
        setTimeline([]);
      }
    } catch (e: any) {
      console.error('Dashboard load error:', e);
      setError(e.message || 'Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDashboard();
  }, []);

  if (!user) {
    window.location.href = '/login';
    return null;
  }

  const totalAvailable = cashflow.reduce((sum, c) => sum + parseFloat(c.currentBalance || '0'), 0);
  const periodNet = timeline.reduce((sum, t) => sum + parseFloat(t.netFlow || '0'), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm sm:text-base">
          Bienvenido, {user.name || user.email} ({user.role})
        </p>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-4 rounded-lg mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-medium">⚠️ {error}</span>
          <button
            onClick={loadDashboard}
            className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 transition-colors min-h-[44px]"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Primary actions */}
      <section className="mb-6 sm:mb-8">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 uppercase tracking-wide mb-3">Acciones rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link
            to="/pos"
            className="lg:col-span-2 flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-4 sm:py-5 font-bold text-lg sm:text-xl shadow transition-colors min-h-[56px] sm:min-h-[64px]"
          >
            <span className="text-2xl sm:text-3xl">🛒</span>
            Nueva Venta
          </Link>
          {!isCashier && (
            <Link
              to="/customers"
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-5 py-4 font-semibold text-base shadow transition-colors min-h-[56px]"
            >
              <span className="text-xl">👤</span>
              Nuevo Cliente
            </Link>
          )}
          {!isCashier && (
            <Link
              to="/products"
              className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-5 py-4 font-semibold text-base shadow transition-colors min-h-[56px]"
            >
              <span className="text-xl">📦</span>
              Nuevo Producto
            </Link>
          )}
        </div>
      </section>

      {/* KPI cards */}
      {!isCashier && (
      <section className="mb-6 sm:mb-8">
        <h2 className="text-lg font-bold mb-3">Resumen</h2>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : !stats ? (
          <EmptyState message="No hay datos disponibles" onRetry={loadDashboard} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Ventas de Hoy"
              value={formatMoney(stats.today?.totalSales)}
              subtitle={`${formatNumber(stats.today?.salesCount)} ventas`}
              icon="💰"
              colorClass="text-blue-600 dark:text-blue-400"
              borderColor="#2563eb"
            />
            <KpiCard
              label="Ventas del Mes"
              value={formatMoney(stats.month?.totalSales)}
              subtitle={`${formatNumber(stats.month?.salesCount)} ventas`}
              icon="📈"
              colorClass="text-purple-600"
              borderColor="#7c3aed"
            />
            <KpiCard
              label="Productos Activos"
              value={formatNumber(stats.inventory?.productCount)}
              subtitle={`${formatNumber(stats.inventory?.lowStockCount)} bajo mínimo`}
              subtitleAccent={(stats.inventory?.lowStockCount || 0) > 0}
              icon="📦"
              colorClass="text-green-600 dark:text-green-400"
              borderColor="#059669"
            />
            <KpiCard
              label="Facturado"
              value={formatMoney(stats.billing?.totalInvoiced)}
              subtitle={`${formatNumber(stats.billing?.pendingInvoices)} facturas pendientes`}
              subtitleAccent={(stats.billing?.pendingInvoices || 0) > 0}
              icon="🧾"
              colorClass="text-amber-500"
              borderColor="#f59e0b"
            />
          </div>
        )}
      </section>
      )}

      {/* Cash overview */}
      {!loading && stats && (
        <section className="mb-6 sm:mb-8">
          <h2 className="text-lg font-bold mb-3">Caja y Acreditaciones</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Cash balances */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">💵 Saldos por Caja</h3>
                <Link
                  to="/cash"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium min-h-[44px] flex items-center px-2"
                >
                  Ver caja →
                </Link>
              </div>
              {cashflow.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 text-sm text-center py-6">No hay cajas configuradas.</p>
              ) : (
                <>
                  <div className="space-y-3">
                    {cashflow.map((c) => {
                      const balance = parseFloat(c.currentBalance || '0');
                      return (
                        <div
                          key={c.registerId}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getRegisterIcon(c.type)}</span>
                            <div>
                              <p className="font-semibold text-sm text-gray-800 dark:text-gray-100">{c.name}</p>
                              {c.pendingSettlements > 0 && (
                                <p className="text-xs text-amber-600 font-medium">
                                  ⏳ {c.pendingSettlements} pend.
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-left sm:text-right">
                            <p className={`text-lg font-bold ${balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600'}`}>
                              {formatMoney(balance)}
                            </p>
                            {c.pendingSettlements > 0 && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
                                +{formatMoney(c.pendingNet)} → {formatMoney(c.projectedBalance)}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <span className="font-bold text-gray-800 dark:text-gray-100">Total Disponible</span>
                    <span className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">{formatMoney(totalAvailable)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Upcoming accreditation timeline */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">📅 Acreditaciones Próximas</h3>
              {timeline.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 text-sm text-center py-6">No hay acreditaciones pendientes.</p>
              ) : (
                <>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                    {timeline.slice(0, 5).map((t) => {
                      const isDue = new Date(t.date) <= new Date();
                      const net = parseFloat(t.netFlow || '0');
                      return (
                        <div key={t.date} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">
                                {new Date(t.date).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                              </span>
                              {isDue && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-500 text-white">
                                  Vencido
                                </span>
                              )}
                            </div>
                            <span className={`font-bold ${net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600'}`}>
                              {net >= 0 ? '+' : ''}{formatMoney(net)}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {(t.inflows || []).map((r, i) => (
                              <p key={`in-${i}`} className="text-xs text-green-600 dark:text-green-400">
                                + {r.registerName || r.type}: {formatMoney(r.net || r.amount)}
                              </p>
                            ))}
                            {(t.outflows || []).map((r, i) => (
                              <p key={`out-${i}`} className="text-xs text-red-600">
                                - {r.poNumber || r.type}: {formatMoney(r.amount)}
                              </p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <span className="font-bold text-gray-800 dark:text-gray-100">Balance neto del período</span>
                    <span className={`text-lg font-bold ${periodNet >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600'}`}>
                      {formatMoney(periodNet)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Module access */}
      {!loading && stats && (
        <section>
          <h2 className="text-lg font-bold mb-3">Módulos</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {modules.map((mod) => (
              <Link
                key={mod.name}
                to={mod.path}
                className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow hover:shadow-md transition-shadow flex items-center gap-4 min-h-[72px]"
              >
                <span className="text-3xl">{mod.icon}</span>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{mod.name}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
